<template>
    <HorizontalPopover componentId="shortcuts">
        <template #reference>
            <button
                @click="fetchShortcuts"
                class="flex transform hover:scale-105 transition duration-300 p-2 text-sm md:text-base xs:whitespace-nowrap"
            >
                <p v-text="$lang.get('My shortcuts')" class="" />
            </button>
        </template>
        <template #floating>
            <div class="w-[88vw] xs:w-screen xs:max-w-sm md:max-w-md text-text">
                <div
                    class="relative bg-white pt-1.5 xs:pt-6 p-2 xs:p-7 flex flex-col"
                >
                    <!-- <ReactiveHeightTransition> -->
                    <div v-if="!loading" class="flex flex-col p-1">
                        <Tabs
                            :tabs="tabs"
                            :shouldFit="true"
                            :selectedTab="selectedTabIndex"
                        >
                            <template
                                v-for="tab in tabs"
                                v-slot:[tab.id]
                                :tab="tab"
                            >
                                <div
                                    class="space-y-4 overflow-y-auto max-h-[50vh]"
                                >
                                    <ModelPreview
                                        v-if="tab.data.length > 0"
                                        v-for="model in tab.data"
                                        @remove="
                                            () =>
                                                tab.id === 'favorites'
                                                    ? onRemove(model)
                                                    : null
                                        "
                                        :key="model.id"
                                        :imageSrc="model.imageUrl"
                                        :modelClass="model.modelClass"
                                        imageSize="size-16"
                                        imageRounded="rounded-md rounded-r-none"
                                        imageBorder=""
                                        componentPadding=""
                                        contentPadding="py-1"
                                        componentBorder="border border-gray-100"
                                        :showRemoveButton="
                                            tab.id === 'favorites'
                                        "
                                        :modelName="model.name"
                                        :routeUrl="$getModelUrl(model)"
                                        :typeId="model.type_id"
                                        :fileUrl="model.file"
                                    />
                                    <p
                                        v-else-if="tab.id === 'favorites'"
                                        class="italic font-light"
                                        v-text="
                                            $lang.get(
                                                'Add favorites to have them appear here'
                                            )
                                        "
                                    />
                                </div>
                            </template>
                        </Tabs>
                        <div
                            class="fixed top-5.5 right-5 flex justify-center items-center"
                        >
                            <SecondaryButton
                                size="xs"
                                @click="openNetworkSettings"
                                v-if="selectedTab.id === 'network' && false"
                            >
                                <PhGear class="size-8" />
                            </SecondaryButton>
                        </div>
                    </div>
                    <div
                        v-else
                        class="flex flex-col justify-center items-center p-5"
                    >
                        <PhCircleNotch
                            class="size-8 animate-spin text-primary"
                        />
                        <p
                            class="animate-pulse"
                            v-text="$lang.get('Fetching favorites')"
                        />
                    </div>
                    <!-- </ReactiveHeightTransition> -->
                </div>
            </div>
        </template>
    </HorizontalPopover>
    <FavoriteSettingModal ref="favoriteSetting" />
    <ConfirmDangerModal ref="dangerModal" />
</template>

<script>
import ModelImage from '@/Components/ModelComponents/ModelImage.vue'
import ModelPreview from '@/Components/ModelComponents/ModelPreview.vue'
import ModelClassTag from '@/Components/ModelComponents/ModelClassTag.vue'
import HorizontalPopover from '@/Components/Utilities/HorizontalPopover.vue'
import ReactiveHeightTransition from '@/Components/Transitions/ReactiveHeightTransition.vue'
import SecondaryButton from '@/Components/Elements/SecondaryButton.vue'
import FavoriteSettingModal from '@/Components/Profile/FavoriteSettingModal.vue'
import Tabs from '@/Components/Utilities/Tabs.vue'
import ConfirmDangerModal from '@/Components/Modals/ConfirmDangerModal.vue'

import { PhGear, PhCircleNotch, PhPencilSimple } from '@phosphor-icons/vue'

import { Link } from '@inertiajs/vue3'

// pinia
import { mapActions, mapState } from 'pinia'
import { shortcutStore } from '@/Store/shortcuts'

export default {
    name: 'ShortCutPopover',
    components: {
        ModelImage,
        ModelClassTag,
        Link,
        HorizontalPopover,
        ReactiveHeightTransition,
        SecondaryButton,
        PhGear,
        PhCircleNotch,
        PhPencilSimple,
        FavoriteSettingModal,
        ModelPreview,
        Tabs,
        ConfirmDangerModal,
    },

    data() {
        return {
            selectedTabIndex: 0,
        }
    },

    computed: {
        ...mapState(shortcutStore, [
            'favorites',
            'organizations',
            'membershipOrganizations',
            'employerOrganizations',
            'loading',
        ]),

        tabs() {
            let tabs = [
                {
                    id: 'favorites',
                    title: this.$lang.get('Favorites'),
                    data: this.favorites,
                },
            ]

            if (this.organizations.length) {
                tabs.push({
                    id: 'organizations',
                    title: this.$lang.get('My :plural_model', {
                        plural_model: this.$getBaseModelTranslation({
                            modelName: 'organization',
                            wordClass: 'plural',
                        }),
                    }),
                    data: this.organizations,
                })
            }

            if (this.membershipOrganizations.length) {
                tabs.push({
                    id: 'groups',
                    title: this.$lang.get('Groups & Teams'),
                    data: this.membershipOrganizations,
                })
            }

            if (this.employerOrganizations.length) {
                tabs.push({
                    id: 'employers',
                    title: this.$lang.get('Employers'),
                    data: this.employerOrganizations,
                })
            }

            return tabs
        },

        selectedTab() {
            return this.tabs[this.selectedTabIndex]
        },
    },

    methods: {
        ...mapActions(shortcutStore, ['fetchShortcuts', 'deleteFavorite']),

        openNetworkSettings() {
            // this.$refs.favoriteSetting.open()
        },

        onRemove(favorite) {
            this.$refs.dangerModal
                .confirm({
                    title: this.$lang.get('Delete :definitive_item_name', {
                        definitive_item_name: this.$lang.get('favorite'),
                    }),
                    body: this.$lang.get(
                        'Are you sure you want to delete :definitive_item_name?',
                        {
                            definitive_item_name:
                                this.$lang.get('the favorite'),
                        }
                    ),
                })
                .then((confirmed) => {
                    if (confirmed) {
                        this.deleteFavorite(favorite)
                    }
                })
        },
    },
}
</script>
